import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Power, SignIn, SpinnerGap, SignOut } from "@phosphor-icons/react";
import { toast } from "sonner";

const ShiftCtx = createContext(null);
const etClock = () => new Date().toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "2-digit", minute: "2-digit", second: "2-digit" });
const etDate = () => new Date().toLocaleDateString("en-US", { timeZone: "America/New_York", weekday: "long", month: "long", day: "numeric" });
const fmt = (iso) => iso ? new Date(iso).toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" }) + " ET" : "";

function Clock() {
  const [t, setT] = useState(etClock());
  useEffect(() => { const i = setInterval(() => setT(etClock()), 1000); return () => clearInterval(i); }, []);
  return <p className="font-mono text-6xl sm:text-7xl font-bold tracking-tight text-white tabular" data-testid="checkin-clock">{t}</p>;
}

function Gate({ user, shift, onCheckIn, busy, onLogout }) {
  const first = (user.name || "").split(" ")[0];
  return (
    <div className="fixed inset-0 z-[100] bg-[#050507] text-white overflow-hidden" data-testid="checkin-gate">
      <div className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full bg-blue-600/20 blur-[120px]" />
      <div className="absolute -bottom-40 -right-40 w-[520px] h-[520px] rounded-full bg-emerald-500/10 blur-[120px]" />
      <div className="relative h-full flex flex-col justify-between p-8 sm:p-14">
        <div className="flex items-center gap-3">
          <img src="/freightx-logo.webp" alt="" className="w-10 h-10 object-contain" />
          <p className="font-heading font-extrabold tracking-tight">Asterism Services</p>
        </div>
        <div className="max-w-3xl animate-fade-up">
          <p className="text-xs uppercase tracking-[0.3em] text-blue-400 font-semibold">{etDate()} · US Eastern</p>
          <Clock />
          <h1 className="mt-6 text-3xl sm:text-4xl font-heading font-extrabold">Good to see you, {first}.</h1>
          <p className="mt-2 text-white/60 max-w-lg">{shift?.check_out_at ? `You checked out at ${fmt(shift.check_out_at)}. Check in again to keep working.` : "Check in to go live. The TMS unlocks as soon as you're on the clock."}</p>
          <button onClick={onCheckIn} disabled={busy} className="mt-10 group inline-flex items-center gap-3 h-16 pl-7 pr-9 rounded-full bg-emerald-500 text-black font-bold text-lg shadow-[0_0_60px_-10px_rgba(16,185,129,0.8)] hover:bg-emerald-400 hover:scale-[1.03] active:scale-95 transition-[transform,background-color] disabled:opacity-60" data-testid="checkin-btn">
            {busy ? <SpinnerGap size={24} className="animate-spin" /> : <Power size={24} weight="bold" />}{busy ? "Checking in…" : "Check In"}
          </button>
        </div>
        <button onClick={onLogout} className="self-start inline-flex items-center gap-2 text-sm text-white/40 hover:text-white transition-colors" data-testid="checkin-logout"><SignOut size={16} />Sign out</button>
      </div>
    </div>
  );
}

export function ShiftGate({ user, onLogout, children }) {
  const [shift, setShift] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get("/attendance/today/me").then(({ data }) => setShift(data)).catch(() => setShift({ checked_in: true })), []);
  useEffect(() => {
    load();
    const i = setInterval(load, 120000);
    window.addEventListener("shift-refresh", load);
    return () => { clearInterval(i); window.removeEventListener("shift-refresh", load); };
  }, [load]);
  const checkIn = async () => {
    setBusy(true);
    try { const { data } = await api.post("/attendance/check-in"); setShift(data); toast.success(`Checked in at ${fmt(data.check_in_at)}. Have a great shift!`); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  if (!shift) return <div className="fixed inset-0 bg-[#050507] grid place-items-center"><SpinnerGap size={28} className="animate-spin text-white/50" /></div>;
  return (
    <ShiftCtx.Provider value={{ shift, setShift }}>
      {shift.checked_in ? children : <Gate user={user} shift={shift} onCheckIn={checkIn} busy={busy} onLogout={onLogout} />}
    </ShiftCtx.Provider>
  );
}

export function ShiftButton() {
  const ctx = useContext(ShiftCtx);
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  if (!ctx) return null;
  const out = async () => {
    setBusy(true);
    try { const { data } = await api.post("/attendance/check-out", { notes }); setOpen(false); setNotes(""); toast.success("Checked out. See you next shift!"); ctx.setShift(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 h-8 px-3 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold hover:bg-emerald-100 transition-colors" title={`Checked in ${fmt(ctx.shift.check_in_at)} · click to check out`} data-testid="shift-live-btn">
        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" /><span className="hidden md:inline">Live since {fmt(ctx.shift.check_in_at)}</span><span className="md:hidden">Live</span>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-md" data-testid="checkout-dialog">
          <DialogHeader><DialogTitle className="font-heading">Check out for the day?</DialogTitle><DialogDescription>Add your working notes for today. You'll need to check in again to use the TMS.</DialogDescription></DialogHeader>
          <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={4} placeholder="What did you work on today?" className="bg-white border-slate-300" data-testid="checkout-notes" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-300 bg-white text-slate-700">Stay checked in</Button>
            <Button onClick={out} disabled={busy} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="checkout-confirm"><SignIn size={16} className="mr-1.5 rotate-180" />{busy ? "Checking out…" : "Check Out"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
